import React, { useContext, useState } from 'react'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import InfoIcon from '@mui/icons-material/Info'
import PersonIcon from '@mui/icons-material/Person'
import WorkspacesIcon from '@mui/icons-material/Workspaces'
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import PlaceIcon from '@mui/icons-material/Place'
import { UserContext } from '../../App'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Button,
  TextField,
} from '@mui/material'
import ExpandMore from '@mui/icons-material/ExpandMore'
import WorkHistory from '@mui/icons-material/WorkHistory'
import HowToReg from '@mui/icons-material/HowToReg'
import BigModal from '../../components/Modals/BigModal'
import { Accessible } from '@mui/icons-material'

const ShiftLine = ({
  shift,
  showStatus,
  onClick,
  isSelf,
  acknowledgeShift,
  declineShift,
}) => {
  const { user } = useContext(UserContext)
  const [declineOpen, setDeclineOpen] = useState(false)
  const [declineReason, setDeclineReason] = useState('')
  const [withdrawOpen, setWithdrawOpen] = useState(false)
  const shiftIsHistoric = moment(shift.start).isBefore(moment())
  const acknowledgement = shift?.acknowledgement
  const needsAcknowledgement =
    (!!acknowledgeShift || !!declineShift) &&
    acknowledgement?.required &&
    // On-call shifts never need acknowledgement (covers legacy on-call
    // shifts stamped required before the exemption)
    !shift?.isOnCall &&
    !acknowledgement?.acknowledgedAt &&
    !acknowledgement?.declinedAt
  // Said "cannot attend" but plans have changed - acknowledging withdraws it
  const canWithdrawDecline =
    !!acknowledgeShift &&
    !!acknowledgement?.declinedAt &&
    moment(shift.end).isAfter(moment())

  const handleAcknowledge = (e) => {
    e.stopPropagation()
    acknowledgeShift?.(shift._id)
  }

  const handleDecline = (e) => {
    e.stopPropagation()
    setDeclineOpen(true)
  }

  const handleWithdrawDecline = (e) => {
    e.stopPropagation()
    setWithdrawOpen(true)
  }

  const submitWithdrawDecline = () => {
    acknowledgeShift?.(shift._id)
    setWithdrawOpen(false)
  }

  const submitDecline = () => {
    declineShift?.(shift._id, declineReason)
    setDeclineOpen(false)
    setDeclineReason('')
  }

  return (
    <>
      <Stack
        key={shift._id}
        direction={{ xs: 'column', md: 'row' }}
        gap={1.5}
        padding={1.5}
        alignItems="center"
        sx={{
          cursor: 'pointer',
          borderBottom: '1px solid',
          borderColor: 'divider',
          transition: 'background-color .15s ease',
          '&:last-of-type': {
            borderBottom: 0,
          },
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
        onClick={onClick}
      >
        {!isSelf && (
          <Stack
            width={{ xs: '100%', md: '2rem' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Avatar src={user.photograph} />
          </Stack>
        )}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          gap={1}
          flex={1}
          alignItems={{ xs: 'stretch', md: 'center' }}
          width="100%"
        >
          <Stack gap={1}>
            <Stack direction={{ xs: 'column', lg: 'row' }} gap={{ lg: 1 }}>
              <Typography color="text.primary" fontWeight={700}>
                {moment(shift.start).format('dddd, Do MMMM YYYY')}
              </Typography>
              <Typography color="text.secondary">
                {moment(shift.start).format('HH:mm')} -{' '}
                {moment(shift.end).format('HH:mm')}
              </Typography>
            </Stack>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {showStatus && (
                <Chip
                  size="small"
                  label={shift.isVerified ? 'Verified' : 'Pending Verification'}
                  color={shift.isVerified ? 'success' : 'error'}
                  variant={shift.isVerified ? 'filled' : 'outlined'}
                  icon={shift.isVerified ? <WorkHistory /> : <HowToReg />}
                  sx={{ borderRadius: 1 }}
                />
              )}
              {!shift?.isVerified &&
                ['Potential', 'Interested', 'Quote Sent'].includes(
                  shift?.event?.status,
                ) && (
                  <Chip
                    size="small"
                    label="TBC"
                    color="anOrange"
                    sx={{ borderRadius: 1 }}
                    icon={<WorkHistory />}
                  />
                )}
              {acknowledgement?.acknowledgedAt && (
                <Chip
                  size="small"
                  label="Acknowledged"
                  color="success"
                  variant="outlined"
                  icon={<CheckCircleIcon />}
                  sx={{ borderRadius: 1 }}
                />
              )}
              {acknowledgement?.declinedAt && (
                <Chip
                  size="small"
                  label="Cannot Attend"
                  color="error"
                  variant="outlined"
                  icon={<HelpOutlineIcon />}
                  sx={{ borderRadius: 1 }}
                />
              )}
              {needsAcknowledgement && (
                <Chip
                  size="small"
                  label="Acknowledgement Required"
                  color="warning"
                  variant="outlined"
                  icon={<HelpOutlineIcon />}
                  sx={{ borderRadius: 1 }}
                />
              )}
              {shift.availableForReassignment && !shiftIsHistoric && (
                <Chip
                  size="small"
                  label="Available for Reassignment"
                  color="anRed"
                  variant="contained"
                  sx={{ borderRadius: 1 }}
                  icon={<EmojiPeopleIcon />}
                />
              )}
              {shift.event && (
                <Chip
                  size="small"
                  label={shift?.event?.title}
                  color="anPurple"
                  variant="outlined"
                  icon={<WorkspacesIcon />}
                  sx={{ borderRadius: 1 }}
                />
              )}
              {shift.roster && (
                <Chip
                  size="small"
                  label={shift?.roster?.title}
                  color="anPurple"
                  variant="outlined"
                  icon={<WorkspacesIcon />}
                  sx={{ borderRadius: 1 }}
                />
              )}
              {shift.shiftType === 'PTS' && (
                <Chip
                  size="small"
                  label="PTS"
                  color="anPurple"
                  variant="outlined"
                  sx={{ borderRadius: 1 }}
                  icon={<Accessible />}
                />
              )}
              <Chip
                size="small"
                label={shift.clinicalRole}
                color="anGreen"
                icon={<PersonIcon />}
                sx={{ borderRadius: 1 }}
                variant="outlined"
              />
              {shift?.event?.eventLocation && (
                <Box display="flex" flex={1}>
                  <Chip
                    size="small"
                    label={shift?.event?.eventLocation}
                    color="primary"
                    icon={<PlaceIcon />}
                    sx={{ borderRadius: 1 }}
                    variant="outlined"
                  />
                </Box>
              )}
            </Stack>
            {needsAcknowledgement && (
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={handleAcknowledge}
                >
                  Acknowledge
                </Button>
                <Button size="small" color="error" onClick={handleDecline}>
                  Cannot Attend
                </Button>
              </Stack>
            )}
            {canWithdrawDecline && (
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  onClick={handleWithdrawDecline}
                >
                  I can attend after all
                </Button>
              </Stack>
            )}
          </Stack>
          <NavigateNextIcon
            sx={{ alignSelf: { xs: 'flex-end', md: 'center' } }}
          />
        </Stack>
      </Stack>
      <BigModal
        open={declineOpen}
        onClose={() => setDeclineOpen(false)}
        title="Cannot Attend"
      >
        <Stack gap={2}>
          <Alert severity="warning">
            Your managers will be told you cannot attend, and the shift will be
            offered to colleagues on Available Shifts.{' '}
            <strong>
              This does not remove you from the shift - you stay assigned and
              responsible for it until a manager reassigns it.
            </strong>
          </Alert>
          <TextField
            label="Reason"
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            multiline
            minRows={3}
            fullWidth
          />
          <Stack direction="row" justifyContent="flex-end" gap={1}>
            <Button onClick={() => setDeclineOpen(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={submitDecline}>
              Submit
            </Button>
          </Stack>
        </Stack>
      </BigModal>
      <BigModal
        open={withdrawOpen}
        onClose={() => setWithdrawOpen(false)}
        title="I can attend after all"
      >
        <Stack gap={2}>
          <Alert severity="info">
            This confirms you will work this shift. Your managers will be told,
            and if saying you couldn&apos;t attend put the shift up for a swap,
            that offer is withdrawn and any colleagues who applied are told it
            is no longer available.
          </Alert>
          <Stack direction="row" justifyContent="flex-end" gap={1}>
            <Button onClick={() => setWithdrawOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              onClick={submitWithdrawDecline}
            >
              Confirm I can attend
            </Button>
          </Stack>
        </Stack>
      </BigModal>
    </>
  )
}

export default ShiftLine
